const TNVED_MIN_DIGITS = 10

const isNonEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  return true
}

const tnvedDigits = (value: unknown): string => {
  if (!isNonEmpty(value)) {
    return ''
  }
  return String(value).replace(/\D/g, '')
}

const hasPackagingData = (row: CalculationDocumentDraftProduct): boolean => {
  if (row.volume_manual) {
    return true
  }
  if (row.net_weight_manual || row.gross_weight_manual) {
    return true
  }
  if (row.net_weight_cartoon || row.gross_weight_cartoon) {
    return true
  }
  if (row.length && row.width && row.height) {
    return true
  }
  return false
}

const productMissingFields = (
  row: CalculationDocumentDraftProduct,
  docKind: CalculationDocumentDocKind,
): string[] => {
  const missing: string[] = []

  if (!isNonEmpty(row.name)) {
    missing.push('name')
  }
  if (!isNonEmpty(row.country_code)) {
    missing.push('country_code')
  }
  if (tnvedDigits(row.code).length < TNVED_MIN_DIGITS) {
    missing.push('code')
  }
  if (!row.pieces) {
    missing.push('pieces')
  }
  if (!row.places_manual) {
    missing.push('places_manual')
  }
  if (!row.pieces_in_cartoon) {
    missing.push('pieces_in_cartoon')
  }
  if (!row.unit_price && !row.price) {
    missing.push('unit_price')
  }
  if (docKind === 'packing' && !hasPackagingData(row)) {
    missing.push('packaging')
  }

  return missing
}

const FIELD_LABELS: Record<string, string> = {
  invoice_num: 'calc__text__main__numOfInvoice',
  invoice_date: 'calc__text__main__dateOfInvoice',
  name: 'calc__text__packaging__nameItem',
  country_code: 'calc__text__packaging__countryOfOrigin',
  code: 'common__text__codeOfHS',
  pieces: 'common__text__totalQuantity',
  places_manual: 'calc__text__documentImport__placesManual',
  pieces_in_cartoon: 'calc__text__packaging__piecesInCartoon',
  unit_price: 'common__text__price',
  packaging: 'calc__text__documentImport__packagingRequired',
}

export const buildReadiness = (
  invoice: CalculationDocumentDraftInvoice | null,
  products: CalculationDocumentDraftProduct[],
  docKind: CalculationDocumentDocKind,
  creatingInvoice: boolean,
): CalculationDocumentDraftReadiness => {
  const invoiceMissing: string[] = []
  if (creatingInvoice) {
    if (!isNonEmpty(invoice?.invoice_num)) {
      invoiceMissing.push('invoice_num')
    }
    if (!isNonEmpty(invoice?.invoice_date)) {
      invoiceMissing.push('invoice_date')
    }
  }

  const productIssues = products
    .map((row, index) => {
      const missing_fields = productMissingFields(row, docKind)
      if (!missing_fields.length) {
        return null
      }
      return {
        index,
        missing_fields,
        missing_labels: missing_fields.map((field) => FIELD_LABELS[field] || field),
      }
    })
    .filter(Boolean) as CalculationDocumentDraftReadinessProduct[]

  return {
    ready: invoiceMissing.length === 0 && productIssues.length === 0,
    invoice_missing_fields: invoiceMissing,
    invoice_missing_labels: invoiceMissing.map((field) => FIELD_LABELS[field] || field),
    products: productIssues,
  }
}

export const isFieldMissingForProduct = (
  readiness: CalculationDocumentDraftReadiness,
  productIndex: number,
  field: string,
): boolean => {
  const row = readiness.products.find((item) => item.index === productIndex)
  return Boolean(row?.missing_fields.includes(field))
}
